// services/complaintService.js
import { addDoc, collection, deleteDoc, doc, getDocs, onSnapshot, updateDoc } from "firebase/firestore";
import { db } from "../network/firebaseConfig";
import { CACHE_KEYS, getCachedData, setCachedData } from "./localStorageService";
const COMPLAINT_TYPES_COLLECTION = "complaintTypes";
const COMPLAINT_CATEGORIES_COLLECTION = "complaintCategories";

// Get all complaint categories
export const getComplaintCategories = async () => {
    try {
        const snapshot = await getDocs(collection(db, COMPLAINT_CATEGORIES_COLLECTION));
        return snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
    } catch (error) {
        console.error("Error fetching categories:", error);
        return [];
    }
};

// Get complaint types for a specific category
export const getComplaintTypesByCategory = async (categoryId) => {
    try {
        const snapshot = await getDocs(
            collection(db, COMPLAINT_TYPES_COLLECTION)
        );
        const types = snapshot.docs
            .map(doc => ({
                id: doc.id,
                ...doc.data()
            }))
            .filter(type => type.categoryId === categoryId);
        return types;
    } catch (error) {
        console.error("Error fetching complaint types:", error);
        return [];
    }
};

// Get all complaint types with real-time updates
export const subscribeToComplaintTypes = (callback) => {
    return onSnapshot(
        collection(db, COMPLAINT_TYPES_COLLECTION),
        (snapshot) => {
            const types = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            callback(types);
        },
        (error) => {
            console.error("Error subscribing to complaint types:", error);
        }
    );
};

// Get all complaint categories with real-time updates
export const subscribeToComplaintCategories = (callback) => {
    return onSnapshot(
        collection(db, COMPLAINT_CATEGORIES_COLLECTION),
        (snapshot) => {
            const categories = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            callback(categories);
        },
        (error) => {
            console.error("Error subscribing to complaint categories:", error);
        }
    );
};



// ================================================================================================
// Add these functions to complaintService.js

// Add a new complaint category
export const addComplaintCategory = async (categoryData) => {
    try {
        const docRef = await addDoc(collection(db, COMPLAINT_CATEGORIES_COLLECTION), {
            ...categoryData,
            createdAt: new Date(),
            updatedAt: new Date()
        });
        return docRef.id;
    } catch (error) {
        console.error("Error adding category:", error);
        throw error;
    }
};

// Update a complaint category
export const updateComplaintCategory = async (categoryId, categoryData) => {
    try {
        await updateDoc(doc(db, COMPLAINT_CATEGORIES_COLLECTION, categoryId), {
            ...categoryData,
            updatedAt: new Date()
        });
    } catch (error) {
        console.error("Error updating category:", error);
        throw error;
    }
};

// Delete a complaint category
export const deleteComplaintCategory = async (categoryId) => {
    try {
        await deleteDoc(doc(db, COMPLAINT_CATEGORIES_COLLECTION, categoryId));
    } catch (error) {
        console.error("Error deleting category:", error);
        throw error;
    }
};

// Get all complaint types (without filtering)
export const getComplaintTypes = async () => {
    try {
        const snapshot = await getDocs(collection(db, COMPLAINT_TYPES_COLLECTION));
        return snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
    } catch (error) {
        console.error("Error fetching complaint types:", error);
        return [];
    }
};

// Add a new complaint type
export const addComplaintType = async (typeData) => {
    try {
        const docRef = await addDoc(collection(db, COMPLAINT_TYPES_COLLECTION), {
            ...typeData,
            createdAt: new Date(),
            updatedAt: new Date()
        });
        return docRef.id;
    } catch (error) {
        console.error("Error adding complaint type:", error);
        throw error;
    }
};

// Update a complaint type
export const updateComplaintType = async (typeId, typeData) => {
    try {
        await updateDoc(doc(db, COMPLAINT_TYPES_COLLECTION, typeId), {
            ...typeData,
            updatedAt: new Date()
        });
    } catch (error) {
        console.error("Error updating complaint type:", error);
        throw error;
    }
};

// Delete a complaint type
export const deleteComplaintType = async (typeId) => {
    try {
        await deleteDoc(doc(db, COMPLAINT_TYPES_COLLECTION, typeId));
    } catch (error) {
        console.error("Error deleting complaint type:", error);
        throw error;
    }
};
// ======================================================================================

// Load categories with caching
export const loadCategories = async () => {
    // Check cache first
    const cached = getCachedData(CACHE_KEYS.CATEGORIES);
    if (cached) {
        return cached;
    }

    // Fetch from Firebase using existing getComplaintCategories
    const categories = await getComplaintCategories();
    setCachedData(CACHE_KEYS.CATEGORIES, categories);
    return categories;
};

// Load complaint types with caching
export const loadComplaintTypes = async () => {
    // Check cache first
    const cached = getCachedData(CACHE_KEYS.TYPES);
    if (cached) {
        return cached;
    }

    // Fetch from Firebase
    const snapshot = await getDocs(collection(db, COMPLAINT_TYPES_COLLECTION));
    const types = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
    }));
    setCachedData(CACHE_KEYS.TYPES, types);
    return types;
};

// Load all data (categories + types)
export const loadAllComplaintData = async () => {
    try {
        const [categories, types] = await Promise.all([
            loadCategories(),
            loadComplaintTypes()
        ]);
        return { categories, types };
    } catch (error) {
        console.error("Error loading complaint data:", error);
        throw error;
    }
};

// Force refresh data (clear cache and fetch fresh)
export const refreshComplaintData = async () => {
    const { clearCache } = await import('./localStorageService');
    clearCache(); // Clear all cache
    return loadAllComplaintData(); // Fetch fresh
};

// Get complaint types for a category from cache (or fallback to API)
export const getComplaintTypesForCategoryCached = async (categoryId) => {
    try {
        // Try to get from cache first
        const types = getCachedData(CACHE_KEYS.TYPES);
        if (types) {
            return types.filter(type => type.categoryId === categoryId);
        }

        // If not in cache, fetch from Firebase
        return await getComplaintTypesByCategory(categoryId);
    } catch (error) {
        console.error("Error getting complaint types:", error);
        return [];
    }
};

// Get categories from cache (or fallback to API)
export const getCategoriesCached = async () => {
    try {
        const categories = getCachedData(CACHE_KEYS.CATEGORIES);
        if (categories) {
            return categories;
        }
        return await getComplaintCategories();
    } catch (error) {
        console.error("Error getting categories:", error);
        return [];
    }
};

// Optional: Add a function to check if cache is valid
export const isComplaintDataCached = () => {
    const categories = getCachedData(CACHE_KEYS.CATEGORIES);
    const types = getCachedData(CACHE_KEYS.TYPES);
    return categories !== null && types !== null;
};
